import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ACTIVE_SESSION_STORAGE_KEY,
  DEFAULT_SIDEBAR_WIDTH,
  MAX_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  OPEN_STORAGE_KEY,
  WIDTH_STORAGE_KEY,
} from '../constants';
import { aiChatService } from '../services/AIChatService';
import { getViewContext } from '../../core/services/viewContext';
import type { ChatSession } from '../types';

interface AIChatContextValue {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  width: number;
  setWidth: (width: number) => void;
  sessions: ChatSession[];
  activeSessionId: number | null;
  setActiveSessionId: (id: number | null) => void;
  createSession: (assistantRoleKey?: string | null) => Promise<void>;
  removeSession: (id: number) => Promise<void>;
  renameSession: (id: number, title: string) => Promise<void>;
  /** Reflects a server-generated title (`sendMessage`'s first-message response) into the sidebar without a full session refetch. */
  setSessionTitle: (id: number, title: string) => void;
}

const AIChatContext = createContext<AIChatContextValue | undefined>(undefined);

function readStoredWidth(): number {
  const stored = Number(sessionStorage.getItem(WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored >= MIN_SIDEBAR_WIDTH && stored <= MAX_SIDEBAR_WIDTH
    ? stored
    : DEFAULT_SIDEBAR_WIDTH;
}

function readStoredActiveSessionId(): number | null {
  const stored = Number(sessionStorage.getItem(ACTIVE_SESSION_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? stored : null;
}

/**
 * Sidebar open/closed, width, and session list/selection — persisted to
 * `sessionStorage` the same way `ProjectContext` persists `activeProject`,
 * so a refresh doesn't lose an open sidebar or its active chat.
 */
export function AIChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(() => sessionStorage.getItem(OPEN_STORAGE_KEY) === 'true');
  const [width, setWidthState] = useState(readStoredWidth);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionIdState] = useState<number | null>(readStoredActiveSessionId);

  useEffect(() => {
    sessionStorage.setItem(OPEN_STORAGE_KEY, String(isOpen));
  }, [isOpen]);

  useEffect(() => {
    sessionStorage.setItem(WIDTH_STORAGE_KEY, String(width));
  }, [width]);

  useEffect(() => {
    if (activeSessionId == null) sessionStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
    else sessionStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, String(activeSessionId));
  }, [activeSessionId]);

  // Loaded lazily on first open, not at app boot, so users who never open
  // the assistant never pay for the request.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    aiChatService.listSessions().then((list) => {
      if (!cancelled) setSessions(list);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const createSession = async (assistantRoleKey?: string | null) => {
    // Closes the gap so the session's system prompt (§3) has a project to
    // frame itself around when started from a project-scoped page.
    // `assistantRoleKey` binds the session to a role-based AI Assistant
    // (docs/AI-Assistant-Roles.md §5.4/§7.2) — omitted for the general assistant.
    const session = await aiChatService.createSession(getViewContext().projectId, assistantRoleKey);
    setSessions((prev) => [session, ...prev]);
    setActiveSessionIdState(session.id);
  };

  const removeSession = async (id: number) => {
    await aiChatService.deleteSession(id);
    setSessions((prev) => prev.filter((s) => s.id !== id));
    setActiveSessionIdState((prev) => (prev === id ? null : prev));
  };

  const renameSession = async (id: number, title: string) => {
    const updated = await aiChatService.renameSession(id, title);
    setSessions((prev) => prev.map((s) => (s.id === id ? updated : s)));
  };

  const setSessionTitle = (id: number, title: string) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, title } : s)));
  };

  const value = useMemo<AIChatContextValue>(
    () => ({
      isOpen,
      toggle: () => setIsOpen((o) => !o),
      close: () => setIsOpen(false),
      width,
      setWidth: (w) => setWidthState(Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, w))),
      sessions,
      activeSessionId,
      setActiveSessionId: setActiveSessionIdState,
      createSession,
      removeSession,
      renameSession,
      setSessionTitle,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, width, sessions, activeSessionId],
  );

  return <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>;
}

export function useAIChatContext(): AIChatContextValue {
  const ctx = useContext(AIChatContext);
  if (!ctx) throw new Error('useAIChatContext must be used within an AIChatProvider');
  return ctx;
}
